import { Exercise, MuscleGroup, EquipmentReference } from '../../types';

export const mockMuscleGroups: MuscleGroup[] = [
  { id: 'quadriceps', name: 'Quadriceps', split: true },
  { id: 'hamstrings', name: 'Hamstrings', split: true },
  { id: 'glutes', name: 'Glutes', split: true },
  { id: 'calves', name: 'Calves', split: true },
  { id: 'chest', name: 'Chest', split: false },
  { id: 'back', name: 'Back', split: true },
  { id: 'shoulders', name: 'Shoulders', split: true },
  { id: 'biceps', name: 'Biceps', split: true },
  { id: 'triceps', name: 'Triceps', split: true },
  { id: 'forearms', name: 'Forearms', split: true },
  { id: 'core', name: 'Core', split: false },
  { id: 'traps', name: 'Traps', split: false },
];

export const mockEquipment: EquipmentReference[] = [
  { id: 'barbell', name: 'Barbell' },
  { id: 'dumbbells', name: 'Dumbbells' },
  { id: 'squat-rack', name: 'Squat Rack' },
  { id: 'bench', name: 'Bench' },
  { id: 'cable-machine', name: 'Cable Machine' },
  { id: 'pull-up-bar', name: 'Pull-up Bar' },
  { id: 'kettlebell', name: 'Kettlebell' },
  { id: 'resistance-bands', name: 'Resistance Bands' },
  { id: 'treadmill', name: 'Treadmill' },
  { id: 'rowing-machine', name: 'Rowing Machine' },
];

export const mockExercises: Exercise[] = [
  {
    id: '1',
    name: 'Barbell Back Squat',
    repRange: [6, 12],
    sets: 3,
    restTime: 180,
    difficulty: 7,
    rating: 9,
    danger: 6,
    symmetryMode: 'symmetrical',
    type: 'compound',
    muscleActivations: { 'quadriceps': 90, 'glutes': 85, 'hamstrings': 70, 'core': 50 },
    equipment: { 'barbell': 1, 'squat-rack': 1 },
    targetAdaptations: { 'hypertrophy': 8, 'strength': 9 },
    instructions: [
      'Set up barbell in squat rack at chest height',
      'Position bar on upper trapezius',
      'Unrack and take 2-3 steps back',
      'Squat down by pushing hips back and bending knees',
      'Descend until thighs are parallel to floor',
      'Drive through heels to return to start position'
    ],
    techniqueChecklist: [
      'Maintain neutral spine throughout movement',
      'Keep knees tracking over toes',
      'Full depth - hip crease below knee cap',
      'Controlled descent and explosive ascent'
    ],
  },
  {
    id: '2',
    name: 'Dumbbell Bicep Curls',
    repRange: [8, 15],
    sets: 3,
    restTime: 60,
    difficulty: 3,
    rating: 7,
    danger: 2,
    symmetryMode: 'bilateral',
    type: 'isolation',
    muscleActivations: { 'biceps': 95, 'forearms': 40 },
    equipment: { 'dumbbells': 2 },
    targetAdaptations: { 'hypertrophy': 9, 'strength': 6 },
    instructions: [
      'Stand with feet hip-width apart',
      'Hold dumbbells at sides with palms facing forward',
      'Curl weights up by flexing biceps',
      'Squeeze at the top of the movement',
      'Lower weights slowly to starting position'
    ],
    techniqueChecklist: [
      'No swinging or momentum',
      'Elbows remain stationary at sides',
      'Full range of motion',
      'Controlled eccentric phase'
    ],
  },
  {
    id: '3',
    name: 'Pull-ups',
    repRange: [5, 12],
    sets: 3,
    restTime: 120,
    difficulty: 8,
    rating: 9,
    danger: 4,
    symmetryMode: 'symmetrical',
    type: 'compound',
    muscleActivations: { 'back': 90, 'biceps': 70, 'shoulders': 60, 'core': 40 },
    equipment: { 'pull-up-bar': 1 },
    targetAdaptations: { 'strength': 9, 'hypertrophy': 7 },
    instructions: [
      'Hang from pull-up bar with overhand grip',
      'Hands slightly wider than shoulder-width',
      'Pull body up until chin clears the bar',
      'Lower with control to full arm extension',
      'Repeat for desired reps'
    ],
    techniqueChecklist: [
      'Full range of motion',
      'No kipping or swinging',
      'Chest up and shoulders back',
      'Controlled descent'
    ],
  },
  {
    id: '4',
    name: 'Bench Press',
    repRange: [6, 10],
    sets: 4,
    restTime: 180,
    difficulty: 6,
    rating: 9,
    danger: 7,
    symmetryMode: 'symmetrical',
    type: 'compound',
    muscleActivations: { 'chest': 90, 'triceps': 70, 'shoulders': 60 },
    equipment: { 'barbell': 1, 'bench': 1 },
    targetAdaptations: { 'strength': 9, 'hypertrophy': 8 },
    instructions: [
      'Lie on bench with feet flat on floor',
      'Grip bar slightly wider than shoulder-width',
      'Unrack bar and position over chest',
      'Lower bar to chest with control',
      'Press bar back to starting position'
    ],
    techniqueChecklist: [
      'Maintain arch in lower back',
      'Keep shoulder blades retracted',
      'Bar touches chest at nipple line',
      'Full lockout at top'
    ],
  },
  {
    id: '5',
    name: 'Deadlift',
    repRange: [3, 8],
    sets: 3,
    restTime: 240,
    difficulty: 9,
    rating: 10,
    danger: 8,
    symmetryMode: 'symmetrical',
    type: 'compound',
    muscleActivations: { 
      'hamstrings': 90, 
      'glutes': 85, 
      'back': 80, 
      'traps': 70, 
      'core': 65,
      'forearms': 60 
    },
    equipment: { 'barbell': 1 },
    targetAdaptations: { 'strength': 10, 'hypertrophy': 7 },
    instructions: [
      'Stand with feet hip-width apart',
      'Bar over mid-foot, close to shins',
      'Grip bar with hands just outside legs',
      'Keep chest up and back straight',
      'Drive through heels and extend hips',
      'Stand tall with shoulders back'
    ],
    techniqueChecklist: [
      'Neutral spine throughout lift',
      'Bar stays close to body',
      'Hip hinge movement pattern',
      'Full hip and knee extension at top'
    ],
  },
  {
    id: '6',
    name: 'Overhead Press',
    repRange: [6, 10],
    sets: 3,
    restTime: 150,
    difficulty: 7,
    rating: 8,
    danger: 5,
    symmetryMode: 'symmetrical',
    type: 'compound',
    muscleActivations: { 'shoulders': 90, 'triceps': 70, 'core': 60, 'back': 40 },
    equipment: { 'barbell': 1 },
    targetAdaptations: { 'strength': 8, 'hypertrophy': 7 },
    instructions: [
      'Stand with feet shoulder-width apart',
      'Bar at shoulder height in front rack position',
      'Press bar straight up overhead',
      'Lock out arms fully at top',
      'Lower bar with control to shoulders'
    ],
    techniqueChecklist: [
      'Tight core throughout movement',
      'Vertical bar path',
      'Full overhead lockout',
      'No excessive back arch'
    ],
  },
  {
    id: '7',
    name: 'Plank',
    repRange: [1, 1],
    sets: 3,
    restTime: 60,
    difficulty: 4,
    rating: 7,
    danger: 1,
    symmetryMode: 'symmetrical',
    type: 'isolation',
    muscleActivations: { 'core': 95, 'shoulders': 40, 'glutes': 30 },
    equipment: {},
    targetAdaptations: { 'endurance': 9, 'strength': 5 },
    instructions: [
      'Start in push-up position',
      'Lower to forearms',
      'Keep body in straight line',
      'Hold position for time',
      'Breathe normally throughout'
    ],
    techniqueChecklist: [
      'Straight line from head to heels',
      'No sagging hips',
      'Engage core muscles',
      'Neutral neck position'
    ],
  },
  {
    id: '8',
    name: 'Romanian Deadlift',
    repRange: [8, 12],
    sets: 3,
    restTime: 120,
    difficulty: 6,
    rating: 8,
    danger: 5,
    symmetryMode: 'symmetrical',
    type: 'compound',
    muscleActivations: { 'hamstrings': 95, 'glutes': 80, 'back': 60, 'core': 50 },
    equipment: { 'barbell': 1 },
    targetAdaptations: { 'hypertrophy': 9, 'strength': 6 },
    instructions: [
      'Stand holding barbell with overhand grip',
      'Feet hip-width apart, knees slightly bent',
      'Hinge at hips, pushing them back',
      'Lower bar along legs maintaining contact',
      'Return to standing by driving hips forward'
    ],
    techniqueChecklist: [
      'Maintain neutral spine',
      'Keep bar close to legs',
      'Feel stretch in hamstrings',
      'Hip hinge, not squat movement'
    ],
  },
]; 